// Forked into ../redesign/SearchByImage/SearchByImagePanel.tsx — keep in sync until phase_0 cleanup.
import React, { useState } from 'react';
import styled from 'styled-components';
import { IconImage } from '@bynder/icons';
import {
	Button,
	ModalBase,
	Input,
	Flex,
	textStyles,
	Tooltip,
	token,
	notify,
} from '@bynder/design-system';
import { useTranslate } from '@bynder/localization';
import FileSelector from '../redesign/SearchByImage/FileSelector';
import { isValidUrl } from '../../../helpers/url';
import { SimilarityFieldName, type SimilarityFilter } from '../helpers/types';
import { isReactAssetOverviewActive } from '../helpers/searchUtils';
import { emitSimilaritySearchEvent } from '../NLSSearch/utils';
import { fileToDataUrl, resizeImage } from '../helpers/searchByImage';
import { storeSearchByImageData } from '../storage/SearchByImageUploadService';
import { useSearchMode } from '../SpotlightState';

/** Context for AI: search-by-image (upload a file or paste an image URL) → a similarity search; emits the event in place when on the assetbank, else stashes the image in IndexedDB and redirects to /media. Rendered only when canUseSimilaritySearch. */
export function SearchByImageModal() {
	const { translate } = useTranslate();
	const { isHybridSearchOn } = useSearchMode();

	const [open, setOpen] = useState(false);
	const [imageURL, setImageURL] = useState('');
	const [isSearchingByLink, setIsSearchingByLink] = useState(false);
	const [isUploadingImage, setIsUploadingImage] = useState(false);
	const isInvalidInput = !isValidUrl(imageURL);

	function onClose() {
		setOpen(false);
		setImageURL('');
	}

	const handleUpload = async (file: File[]) => {
		setIsUploadingImage(true);

		const selectedFile = file[0];

		if (!selectedFile.type.startsWith('image/')) {
			setIsUploadingImage(false);
			notify({
				variant: 'error',
				title: translate('spotlight.searchByImage.upload.unsupportedFileType'),
			});

			return;
		}

		try {
			const imageDataURL = await fileToDataUrl(selectedFile);
			const resizedImageDataURL = await resizeImage(imageDataURL);

			if (isReactAssetOverviewActive()) {
				// Already on asset overview page, emit event to trigger search by image with the uploaded image data
				searchForSimilarAssets({
					similarToAssetIds: null,
					similarToImageData: resizedImageDataURL,
					similarToURL: null,
					similarToText: null,
				});
			} else {
				// Search by image upload is done from a page that does not contain the Asset Overview, so we store the image data in the indexedDB and redirect to the asset overview page
				const storedImageDataId = await storeSearchByImageData(resizedImageDataURL);
				const fieldName = SimilarityFieldName.SIMILAR_TO_IMAGE_DATA;
				globalThis.location.href = `/media?${fieldName}=${storedImageDataId}`;
			}
		} catch (err) {
			console.error(err);
			setIsUploadingImage(false);
			notify({
				variant: 'error',
				title: translate('spotlight.searchByImage.upload.error'),
			});
		}
	};

	const searchForSimilarAssets = (similarityFilter: SimilarityFilter) => {
		if (isReactAssetOverviewActive()) {
			emitSimilaritySearchEvent(isHybridSearchOn, similarityFilter);
		} else if (similarityFilter.similarToURL) {
			const searchParams = new URLSearchParams({
				similarToURL: similarityFilter.similarToURL,
			});
			globalThis.location.href = `/media/?${searchParams.toString()}`;
		}

		setImageURL('');
		setOpen(false);
		setIsUploadingImage(false);
		setIsSearchingByLink(false);
	};

	const handleOnChange = (newValue: string) => {
		setImageURL(newValue);
	};

	const handleSearchingByImageLink = () => {
		setIsSearchingByLink(true);
		searchForSimilarAssets({
			similarToAssetIds: null,
			similarToImageData: null,
			similarToURL: imageURL,
			similarToText: null,
		});
	};

	const handleSearchingByImageLinkOnEnter = (e: React.KeyboardEvent<HTMLInputElement>) => {
		if (e.key === 'Enter' && !isInvalidInput) {
			handleSearchingByImageLink();
		}
	};

	return (
		<WrapperSpan>
			<Tooltip content={translate('spotlight.searchByImage')}>
				<Button
					title=""
					variant="clean"
					icon={<IconImage style={{ color: token.gray600a }} />}
					onClick={() => setOpen(true)}
					isPressed={open}
					isSmall
					data-testid="search-by-image-spotlight-button"
				/>
			</Tooltip>

			<ModalBase isOpen={open} onClose={onClose}>
				<ModalBase.Header title={translate('spotlight.searchByImage')} />
				<ModalBase.Content>
					<Flex direction="column" alignItems="center" justifyContent="center" gap="2">
						<Flex justifyContent="space-between" alignItems="center">
							<Paragraph>
								{translate('spotlight.searchByImage.upload.title')}
								<Label>
									{translate('spotlight.searchByImage.upload.subtitle')}
								</Label>
							</Paragraph>

							<FileSelector onFilesSelected={handleUpload} contentTypes={['image/*']}>
								<Button
									title="Upload an image"
									variant="primary"
									isLoading={isUploadingImage}
								>
									{translate('spotlight.searchByImage.upload.button')}
								</Button>
							</FileSelector>
						</Flex>
						<CustomDivider>
							{translate('spotlight.searchByImage.operator')}
						</CustomDivider>
						<Flex gap="4">
							<Input
								value={imageURL}
								onChange={(newValue) => handleOnChange(newValue)}
								onKeyDown={handleSearchingByImageLinkOnEnter}
								placeholder={translate('spotlight.searchByImage.link.placeholder')}
								type="url"
								isInvalid={!!(imageURL && isInvalidInput)}
							/>
							<Button
								title="search for an image by url"
								variant="primary"
								onClick={() => handleSearchingByImageLink()}
								isLoading={isSearchingByLink}
								isDisabled={!imageURL || isInvalidInput}
							>
								{translate('generalSearch')}
							</Button>
						</Flex>
						<LinkToKnowledgeBase
							href="https://support.bynder.com/hc/en-us/articles/13812016590354-Search-by-Image#01HFFDP52BJG65YRJJ6JSSKJQP"
							target="_blank"
						>
							{translate('spotlight.searchByImage.link.instructions')}
						</LinkToKnowledgeBase>
					</Flex>
				</ModalBase.Content>
			</ModalBase>
		</WrapperSpan>
	);
}

const WrapperSpan = styled.span`
	position: absolute;
	top: 50%;
	right: 0px;
	transform: translateY(-50%);
	padding-right: 4px;
`;

const LinkToKnowledgeBase = styled.a`
	${textStyles.paragraphS};

	color: ${token.blue400}!important;
	align-self: flex-start;
	margin-top: ${token.spacing2};
`;

const Paragraph = styled.p`
	${textStyles.paragraphM}
`;

const CustomDivider = styled.div`
	display: flex;
	align-items: center;
	margin: 8px 0;
	width: 100%;

	&:before,
	&:after {
		content: '';
		flex-grow: 1;
		background: #0310261a;
		height: 1px;
		font-size: 0;
		line-height: 0;
		margin: 0 8px;
	}
`;

const Label = styled.label`
	color: ${token.gray600a};
	${textStyles.uiTextS}
`;
